import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Fortaleza de Nossa Senhora dos Prazeres: History and How to Visit (2026)',
  excerpt: 'Fortaleza de Nossa Senhora dos Prazeres on Ilha do Mel: the only 18th-century military monument in Paraná. History, cannons, access by trail or boat, and visiting tips.',
  publishedAt: '2026-04-25',
  author: 'Guia Ilha do Mel',
  tags: ['atracoes', 'fortaleza', 'historia', 'patrimonio', 'nova-brasilia'],
  coverImage: '/images/fortaleza.jpg',
  readingTime: '7 min',
};

# Fortaleza de Nossa Senhora dos Prazeres: History and How to Visit

The **Fortress of Our Lady of Pleasures** is the **only 18th-century military monument in Paraná** and one of the most important historical landmarks on the southern coast of Brazil. Built between 1767 and 1769 to protect Paranaguá Bay from invasions, it is now a National Historical and Artistic Heritage site—and one of the must-see attractions of Ilha do Mel (Honey Island).

If you're searching for "Ilha do Mel Fortress," "Nossa Senhora dos Prazeres Fortress," or "Ilha do Mel history," this guide has it all: the complete history, how to get there, what to see, and tips that only locals know.

---

History of the Fortress

Why was it built?

In the 18th century, Paranaguá Bay was one of the most important trade routes in colonial Brazil. Gold, yerba mate, and wood were shipped from the port of Paranaguá to Europe—attracting pirates and the attention of the Spanish and English crowns.

To protect this wealth, the Portuguese government built the Fortress of Nossa Senhora dos Prazeres between **1767 and 1769**, strategically positioned at the highest point of Ilha do Mel, dominating the entrance to the bay.

Technical specifications

| Characteristic | Value |

| -------------- | ----------------------------------------- |

| **Construction** | 1767–1769 |

| **Function** | Protection of Paranaguá Bay |

| **Walls** | 1.5 meters thick |

| **Cannons** | English and Portuguese (18th and 19th centuries) |

| **Status** | National Historical and Artistic Heritage |

| **Rating** | 4.6/5 (720 reviews) |

---

What to see in Fortaleza

1. Cannon Labyrinth

At the top of the hill, a system of trenches and platforms forms the **Cannon Labyrinth** — a network of passageways between artillery positions that offers 360° panoramic views of the bay.

2. Original Cannons

The fortress preserves **English and Portuguese cannons** from the 18th and 19th centuries, some still on their original platforms. The inscriptions on the cannons tell the story of each piece.

3. Trenches of World War II

During World War II, Ilha do Mel was militarized again. The trenches added during that period still exist and can be visited.

4. Visitor Center

The visitor center offers exhibits on the history of the fortress, the island, and the Paranaguá region. It is open from **9 am to 5 pm**.

5. Panoramic Viewpoint

The viewpoint at the highest point of the fortress offers one of the most impressive views of Ilha do Mel — Paranaguá Bay, the mainland, and the neighboring islands in a 360° panorama.

---

How to get to the Fortress

Option 1: Trail (cheapest option)

Starting from Nova Brasília, the trail to Fortaleza is one of the flattest on the island — practically without any uphill sections.

- **Distance:** 3.7 km (7.4 km round trip)

- **Duration:** 2 hours (round trip)

- **Difficulty:** Easy

- **Can you wear flip-flops:** Yes

**Tip:** The beach trail (4 km) is more beautiful than the inland trail, but it's only accessible at low tide. At high tide, use the inland trail.

Option 2: Boat (more comfortable)

Boats depart from Nova Brasília directly to Fortaleza.

- **Duration:** 10 minutes

- **Cost:** R$ 15–25

- **Advantage:** You arrive rested, you can also return by boat

[Complete guide to the trails of Ilha do Mel →](/blog/trilhas-ilha-do-mel)

---

Visiting tips

| Tip | Why |

| -------------------------------- | ----------------------------------------------------- |

| Visit in the morning | The visitor center closes at 5 PM |

| Combine with Fortaleza Beach | There are 4 km of deserted beach next door |

| Bring water | There are no vendors along the way |

| Sunscreen and hat | Little shade on the trail and at the fortress |

| Reserve the whole day | 2 hours of hiking + 1 hour at the fortress + beach = full day |

---

Fortaleza Beach

Next to the fortress lies **Fortress Beach**, a virtually deserted 4 km stretch of coastline. It's one of the most secluded beaches on the island—perfect for those seeking peace and quiet after a historical visit.

- **Infrastructure:** zero

- **Traffic:** very low

- **Ideal for:** relaxing after visiting the fortress

---

Frequently Asked Questions

**Is there a cost to enter the fortress?**
No, entry is free.

**Is it suitable for children?**
Yes. The trail is flat and the fortress has a safe open space.

**Do you need a guide?**
It's not mandatory, but local guides offer tours with stories that aren't on the signs.

What's the best time to go?

All year round. But at low tide you can hike along the beach, which is more beautiful.

<MapsWidget query="Fortaleza de Nossa Senhora dos Prazeres Ilha do Mel" />

---

Continue exploring

The Fortress is just one of the 7 attractions on Ilha do Mel. Discover the others in the [complete guide to attractions](/what-to-do) and find the [best beaches](/blog/best-beaches) to complete your itinerary.

Want to plan your entire trip? Our [complete guide to Ilha do Mel](/guide-complete) has budget options by profile, tips from locals, and everything you need.